import { useState } from "react";
import { useAxios } from "../../../utils/useAxios";
import { TENANT_DOMAIN } from "../../../utils/domain";
import { ERROR_MESSAGES } from "../../../constant/constant";

/**
 * @typedef {Object} ClassroomData
 * @property {number} teacher_id - The teacher's ID
 * @property {string} name - Classroom name (required, max 255 chars)
 * @property {string|null} [description] - Classroom description (optional, nullable)
 * @property {string|null} [start_date] - Start date in YYYY-MM-DD format (optional, nullable)
 * @property {string|null} [end_date] - End date in YYYY-MM-DD format (optional, nullable, must be >= start_date)
 * @property {'0'|'1'} [status] - Classroom status: '0' for inactive, '1' for active (default: '1')
 */

/**
 * @typedef {Object} CreateClassroomResponse
 * @property {string} message - Success message from the API
 * @property {Object} data - The created classroom data
 * @property {number} data.id - The ID of the created classroom
 * @property {number} data.teacher_id - The teacher's ID
 * @property {string} data.name - Classroom name
 * @property {string|null} data.description - Classroom description
 * @property {string|null} data.start_date - Start date
 * @property {string|null} data.end_date - End date
 * @property {string} data.status - Classroom status
 * @property {string} data.created_at - Creation timestamp
 * @property {string} data.updated_at - Last update timestamp
 */

/**
 * Custom hook to handle classroom creation
 */
export const useCreateClassroom = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);
  const { axiosInstance } = useAxios();

  /**
   * Creates a new classroom
   * @param {ClassroomData} classroomData - The classroom data to create
   * @returns {Promise<CreateClassroomResponse|null>} - Returns the API response or null if failed
   */
  const createClassroom = async ({
    teacher_id,
    name,
    description = null,
    start_date = null,
    end_date = null,
    status = "1",
  }) => {
    setLoading(true);
    setError(null);
    setData(null);

    try {
      const response = await axiosInstance.post(
        `${TENANT_DOMAIN}/classroom/store`,
        {
          teacher_id,
          name,
          description,
          start_date,
          end_date,
          status,
        },
        {
          headers: {
            "Content-Type": "application/json",
          },
        },
      );

      setData(response.data);
      return response.data;
    } catch (err) {
      let firstErrorMessage = "";
      if (err?.response?.data?.errors) {
        firstErrorMessage = Object.values(err?.response?.data?.errors)[0][0];
      }
      if (err?.status === 500) {
        setError(ERROR_MESSAGES.INTERNAL_SERVER);
      } else {
        setError(
          firstErrorMessage ||
            err.response?.data?.message ||
            ERROR_MESSAGES.UNKNOWN,
        );
      }
      return null;
    } finally {
      setLoading(false);
    }
  };

  return {
    createClassroom,
    loading,
    error,
    data,
    setError,
  };
};
